import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../../services/api';
import styles from '../../styles/LoginForm.module.css';

const LoginForm: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember_me: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string>('');
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorRef = useRef<{ general?: string, rateLimit?: string }>({});

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Debug logging for state changes
  useEffect(() => {
    console.log('Errors state changed:', errors);
  }, [errors]);

  useEffect(() => {
    console.log('Rate limit error state changed:', rateLimitError);
  }, [rateLimitError]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setIsSubmitting(true);
    setErrors({});
    setRateLimitError('');
    errorRef.current = {};

    try {
      // Call the API directly instead of using AuthContext login
      const response = await apiService.login(formData);

      if (response.status === 'success') {
        // Only update AuthContext on success
        const user = await apiService.getCurrentUser();
        // Manually update the auth state
        window.location.href = '/dashboard';
      } else if (response.status === 'mfa_required') {
        // Handle MFA case
        window.location.href = '/mfa/verify';
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      console.log('Error response:', error.response);
      console.log('Error data:', error.response?.data);

      const status = error.response?.status;
      const data = error.response?.data || {};

      // Check for rate limit - multiple conditions
      // Check if block_type exists (even if empty string, it should be truthy if it's a non-empty string)
      const hasBlockType = data.block_type && typeof data.block_type === 'string' && data.block_type.length > 0;
      const hasBlockDuration = typeof data.block_duration === 'number' && data.block_duration > 0;
      const hasRateLimit = !!data.rate_limit;
      const messageContainsBlocked = typeof data.message === 'string' &&
        (data.message.toLowerCase().includes('blocked') ||
          data.message.toLowerCase().includes('too many'));

      const isRateLimited = status === 429 ||
        hasBlockType ||
        hasRateLimit ||
        (data.status === 'error' && hasBlockDuration) ||
        messageContainsBlocked;

      console.log('Error details:', {
        status,
        hasBlockType,
        hasBlockDuration,
        hasRateLimit,
        messageContainsBlocked,
        isRateLimited,
        data
      });

      if (isRateLimited) {
        const seconds = hasBlockDuration ? data.block_duration : undefined;
        const minutes = seconds ? Math.ceil(seconds / 60) : undefined;
        const baseMsg = data.message || 'Too many login attempts. Please try again later.';

        // Only add suffix if minutes is calculated and message doesn't already contain time info
        const hasTimeInfo = baseMsg.toLowerCase().includes('minute') ||
          baseMsg.toLowerCase().includes('after') ||
          baseMsg.toLowerCase().includes('try again');
        const suffix = (minutes && !hasTimeInfo) ? ` Try again in ${minutes} minute${minutes > 1 ? 's' : ''}.` : '';
        const finalMessage = `${baseMsg}${suffix}`;

        console.log('Setting rate limit error:', finalMessage);
        // Clear general errors first
        setErrors({});
        errorRef.current.general = '';
        // Set rate limit error
        errorRef.current.rateLimit = finalMessage;
        setRateLimitError(finalMessage);
        setForceUpdate(prev => prev + 1);
      } else {
        const errorMessage = data.message || 'Login failed. Please check your credentials.';
        console.log('Setting general error:', errorMessage);
        // Clear rate limit error first
        setRateLimitError('');
        errorRef.current.rateLimit = '';
        // Set general error
        errorRef.current.general = errorMessage;
        setErrors({ general: errorMessage });
        setForceUpdate(prev => prev + 1);
      }
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleRememberMe = () => {
    setFormData(prev => ({ ...prev, remember_me: !prev.remember_me }));
  };

  const handleGoogleSSO = () => {
    setSsoLoading(true);
    setErrors({});
    setRateLimitError('');
    errorRef.current = {};
    const backendUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    window.location.href = `${backendUrl}/social-auth/login/google-oauth2/`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.scrollContainer}>
        <div className={styles.contentContainer}>
          {/* Left Side - Illustration */}
          <div className={styles.illustrationContainer}>
            <div className={styles.spaceContent}>
              <div className={styles.logoContainer}>
                <img src="/images/wallet-transparent.png" alt="Wallet Logo" width="48" height="48" />
                <span className={styles.logoText}>Lendi Effort Recording</span>
              </div>
              <img src="/images/login-illustration.png" alt="Login Illustration" className={styles.illustration} />
              <div className={styles.featureList}>
                <div className={styles.featureItem}>
                  <div className={styles.featureIconContainer} style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ color: '#10b981' }}
                    >
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path>
                    </svg>

                  </div>
                  <span className={styles.featureText}>Track academic contributions</span>
                </div>
                <div className={styles.featureItem}>
                  <div className={styles.featureIconContainer} style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)' }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ color: '#3b82f6' }}
                    >
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path>
                    </svg>
                  </div>
                  <span className={styles.featureText}>Earn recognition points</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className={styles.formWrapper}>
            <div className={styles.formBackground}>
              <div className={styles.formContainer}>
                {/* Mobile Header */}
                <div className={styles.mobileHeader}>
                  <div className={styles.mobileLogoContainer}>
                    <img src="/images/wallet-transparent.png" alt="Wallet Logo" width="36" height="36" />
                    <span className={styles.mobileLogoText}>Lendi Effort Recording</span>
                  </div>
                </div>

                {/* Desktop Header */}
                <div className={styles.desktopHeader}>
                  <h1 className={styles.formTitle}>Sign In</h1>
                  <p className={styles.formSubtitle}>Faculty Performance Portal</p>
                </div>

                {/* Error Messages */}
                {(errors.general || errorRef.current.general) && (
                  <div className={styles.formErrorContainer}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#b91c1c" className="bi bi-exclamation-triangle" viewBox="0 0 16 16">
                      <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.15.15 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.2.2 0 0 1-.054.06.1.1 0 0 1-.066.017H1.146a.1.1 0 0 1-.066-.017.2.2 0 0 1-.054-.06.18.18 0 0 1 .002-.183L7.884 2.073a.15.15 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767z" />
                      <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                    </svg>
                    <span className={styles.formErrorText} style={{ lineHeight: '1' }}>{errors.general || errorRef.current.general}</span>
                  </div>
                )}

                {((rateLimitError && rateLimitError.trim() !== '') || (errorRef.current.rateLimit && errorRef.current.rateLimit.trim() !== '')) && (
                  <div className={styles.rateLimitContainer}>
                    <div className={styles.rateLimitHeader}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#f59e0b" className="bi bi-lock" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M8 0a4 4 0 0 1 4 4v2.05a2.5 2.5 0 0 1 2 2.45v5a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 2 13.5v-5a2.5 2.5 0 0 1 2-2.45V4a4 4 0 0 1 4-4M4.5 7A1.5 1.5 0 0 0 3 8.5v5A1.5 1.5 0 0 0 4.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 11.5 7zM8 1a3 3 0 0 0-3 3v2h6V4a3 3 0 0 0-3-3" />
                      </svg>

                      <span className={styles.rateLimitTitle} style={{ lineHeight: '1' }}>Security Notice</span>
                    </div>
                    <div className={styles.rateLimitMessage}>{rateLimitError || errorRef.current.rateLimit || ''}</div>
                  </div>
                )}

                {/* Login Form */}
                <form id="loginForm" onSubmit={handleSubmit}>
                  {/* Email Field */}
                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>Institutional Email</label>
                    <div
                      id="emailInputContainer"
                      className={`${styles.inputContainer} ${errors.username ? styles.inputErrorState : ''}`}
                    >
                      <svg style={{ color: '#687076', marginRight: '12px' }} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lucide="mail" className="lucide lucide-mail"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect></svg>

                      <input
                        type="email"
                        name="username"
                        id="emailInput"
                        className={styles.input}
                        placeholder="Institutional Email"
                        value={formData.username}
                        onChange={handleChange}
                        autoComplete="off"
                        required
                      />
                    </div>
                    {errors.username && (
                      <div id="emailError" className={styles.errorContainer} style={{ display: 'flex' }}>
                        <svg style={{ color: '#ef4444', width: '14px', height: '14px' }} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className={styles.errorText}>{errors.username}</span>
                      </div>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>Password</label>
                    <div
                      id="passwordInputContainer"
                      className={`${styles.inputContainer} ${errors.password ? styles.inputErrorState : ''}`}
                    >
                      <svg style={{ color: '#687076', marginRight: '12px' }} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lucide="lock" className="lucide lucide-lock input-icon"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        id="passwordInput"
                        className={styles.input}
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleChange}
                        autoComplete="off"
                        required
                      />
                      <div className={styles.eyeIcon} onClick={togglePasswordVisibility}>
                        {!showPassword ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={styles.toggleIcon}
                            style={{ color: '#687076' }}
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={styles.toggleIcon}
                            style={{ color: '#687076' }}
                          >
                            <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path>
                            <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path>
                            <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path>
                            <path d="m2 2 20 20"></path>
                          </svg>
                        )}
                      </div>
                    </div>
                    {errors.password && (
                      <div id="passwordError" className={styles.errorContainer} style={{ display: 'flex' }}>
                        <svg style={{ color: '#ef4444', width: '14px', height: '14px' }} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className={styles.errorText}>{errors.password}</span>
                      </div>
                    )}
                  </div>

                  {/* Remember Me & Forgot Password */}
                  <div className={styles.optionsRow}>
                    <div className={styles.rememberMeContainer} onClick={toggleRememberMe}>
                      <div className={`${styles.checkbox} ${formData.remember_me ? styles.checkboxChecked : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lucide="check-circle" color="#1a237e" className="lucide lucide-check-circle">
                          <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                          <path d="m9 11 3 3L22 4"></path>
                        </svg>
                      </div>
                      <span className={styles.rememberMeText}>Remember me</span>
                      <input
                        type="checkbox"
                        name="remember_me"
                        id="rememberMeCheckbox"
                        checked={formData.remember_me}
                        onChange={handleChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <Link to="/resetpassword" className={styles.forgotPasswordText}>Forgot password?</Link>
                  </div>

                  {/* Submit Button or Loading State */}
                  {loading ? (
                    <div id="loadingState" className={styles.loadingState} style={{ display: 'flex' }}>
                      <div className={styles.spinner}></div>
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <button
                      id="submitButton"
                      type="submit"
                      className={styles.submitButton}
                    >
                      <span className={styles.submitButtonText}>Sign In</span>
                    </button>
                  )}
                </form>

                {/* SSO Section */}
                <div className={styles.ssoSection}>
                  <div className={styles.dividerContainer}>
                    <div className={styles.dividerLine}></div>
                    <span className={styles.dividerText}>or</span>
                    <div className={styles.dividerLine}></div>
                  </div>
                  <button
                    type="button"
                    className={styles.ssoButton}
                    onClick={handleGoogleSSO}
                    disabled={ssoLoading || loading}
                  >
                    {ssoLoading ? (
                      <>
                        <div className={styles.ssoSpinner}></div>
                        <span className={styles.ssoButtonText}>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <img src="/images/google.svg" alt="Google" className={styles.ssoIcon} />
                        <span className={styles.ssoButtonText}>Institutional SSO</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className={styles.footer}>
                <p className={styles.footerText}>© 2025 Work Wallet - Faculty Recognition System</p>
                <p className={styles.footerLink}>Need help? <a className={styles.footerLinkText}>Contact support</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;